import IClonable from "../types/IClonable";
import IEquatable from "../types/IEquatable";
import {DateOnly} from "./DateOnly";
import {
	createFromNumbers,
	dateEquals,
	fromUtc,
	partsInTimeZone,
	throwsIfInvalidDate
} from "./DateTimeHelpers";
import {DayOfWeek} from "./DayOfWeek";
import {TimeOnly} from "./TimeOnly";
import {TimeSpan} from "./TimeSpan";

/**
 * A naive (zone-less) wall-clock date and time.
 *
 * The wall clock is stored in the UTC fields of the internal Date. UTC has no DST, so every
 * wall clock is representable (the previous local-field storage silently shifted values landing
 * in the browser zone's spring-forward gap by an hour, and drifted on arithmetic across
 * transitions), getTicks() is exact, and fromTicks() is its inverse.
 */
export class DateTime implements IEquatable<DateTime>, IClonable<DateTime> {
	private static _maxValue: DateTime | null = null;
	private static _minValue: DateTime | null = null;
	private readonly _date: Date;

	/** `date` must carry the wall clock in its UTC fields (an instant's UTC observation). */
	private constructor(date: Date) {
		this._date = new Date(date.getTime());
		throwsIfInvalidDate(this._date, "DateTime");
	}

	get [Symbol.toStringTag](): string {
		return "DateTime";
	}

	[Symbol.toPrimitive](hint: string): number | string {
		//hint: [default|number|string]
		if (hint === "number") {
			return this._date.getTime();
		}

		return this._date.toISOString();
	}

	/**
	 * Adds the specified number of days to the value of this instance.
	 * @param days The number of days to add.
	 * @returns A new DateTime that adds the specified number of days to the value of this instance.
	 */
	addDays(days: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCDate(newDate.getUTCDate() + days);
		return new DateTime(newDate);
	}

	/**
	 * Adds the specified number of hours to the value of this instance.
	 * @param hours The number of hours to add.
	 * @returns A new DateTime that adds the specified number of hours to the value of this instance.
	 */
	addHours(hours: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCHours(newDate.getUTCHours() + hours);
		return new DateTime(newDate);
	}

	endOfDay(): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCHours(23, 59, 59, 999);
		return new DateTime(newDate);
	}

	/**
	 * Adds the specified number of minutes to the value of this instance.
	 * @param minutes The number of minutes to add.
	 * @returns A new DateTime that adds the specified number of minutes to the value of this instance.
	 */
	addMinutes(minutes: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCMinutes(newDate.getUTCMinutes() + minutes);
		return new DateTime(newDate);
	}

	/**
	 * Adds the specified number of months to the value of this instance.
	 * @param months The number of months to add.
	 * @returns A new DateTime that adds the specified number of months to the value of this instance.
	 */
	addMonths(months: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCMonth(newDate.getUTCMonth() + months);
		return new DateTime(newDate);
	}

	/**
	 * Adds the specified number of seconds to the value of this instance.
	 * @param seconds The number of seconds to add.
	 * @returns A new DateTime that adds the specified number of seconds to the value of this instance.
	 */
	addSeconds(seconds: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCSeconds(newDate.getUTCSeconds() + seconds);
		return new DateTime(newDate);
	}

	/**
	 * Adds the specified TimeOnly value to the value of this instance.
	 * @param time The TimeOnly value to add.
	 * @returns A new DateTime that adds the specified time to the value of this instance.
	 */
	addTime(time: TimeOnly): DateTime {
		return new DateTime(new Date(this._date.getTime() + time.getTicks()));
	}

	/**
	 * Adds the specified number of weeks to the value of this instance.
	 * @param weeks The number of weeks to add.
	 * @returns A new DateTime that adds the specified number of weeks to the value of this instance.
	 */
	addWeeks(weeks: number): DateTime {
		return this.addDays(weeks * 7);
	}

	/**
	 * Adds the specified number of years to the value of this instance.
	 * @param years The number of years to add.
	 * @returns A new DateTime that adds the specified number of years to the value of this instance.
	 */
	addYears(years: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setUTCFullYear(newDate.getUTCFullYear() + years);
		return new DateTime(newDate);
	}

	/**
	 * Creates a new object that is a copy of the current instance.
	 * @returns A new object that is a copy of this instance.
	 */
	clone(): DateTime {
		return new DateTime(this._date);
	}

	/**
	 * Compares the value of this instance to a specified DateTime, DateOnly object, null or undefined.
	 * @param other The object to compare to the current instance.
	 * @returns A signed number indicating the relative values:
	 * - Less than 0 if this instance is earlier than the other
	 * - 0 if this instance is equal to the other
	 * - Greater than 0 if this instance is later than the other
	 * - 1 if other is null or undefined
	 */
	compareTo(other: DateTime | DateOnly | null | undefined): number {
		//ticks compare wall clocks exactly for both types, whatever their internal field layout
		return other == null ? 1 : this.getTicks() - other.getTicks();
	}

	/**
	 * Returns the TimeSpan value represented by the difference between the value of this instance and the specified DateTime object.
	 * @param value The DateTime value to subtract.
	 * @returns A TimeSpan value that represents the duration between the value of the current instance and the specified DateTime object.
	 */
	diff(value: DateTime): TimeSpan {
		return TimeSpan.fromTicks(this.getTicks() - value.getTicks());
	}

	/**
	 * Returns a value indicating whether this instance is equal to a specified object.
	 * @param other The object to compare to this instance.
	 * @returns true if value is an instance of DateTime or DateOnly and equals the value of this instance; otherwise, false.
	 */
	equals(other: DateTime | DateOnly | null | undefined): boolean {
		if (other instanceof DateOnly) {
			return this.toDateOnly().equals(other);
		}

		return dateEquals(this._date, other?._date);
	}

	/**
	 * Returns an integer, between 1 and 31, representing the day of the month.
	 * @return {number} The day of the month as a number.
	 */
	getDay(): number {
		return this._date.getUTCDate();
	}

	/**
	 * Returns the day of the week for the current DateTime instance.
	 * @returns A DayOfWeek enum value representing the day of the week.
	 */
	getDayOfWeek(): DayOfWeek {
		return this._date.getUTCDay();
	}

	/**
	 * Returns the hour component of the date represented by this instance.
	 * @returns The hour component, expressed as a value between 0 and 23.
	 */
	getHours(): number {
		return this._date.getUTCHours();
	}

	/**
	 * Returns the last day of the month for the current DateTime instance.
	 * @returns The last day of the month.
	 */
	getLastOfMonth(): number {
		return new Date(Date.UTC(this._date.getUTCFullYear(), this._date.getUTCMonth() + 1, 0)).getUTCDate();
	}

	/**
	 * Returns the milliseconds component of the date represented by this instance.
	 * @returns The milliseconds component, expressed as a value between 0 and 999.
	 */
	getMilliseconds(): number {
		return this._date.getUTCMilliseconds();
	}

	/**
	 * Returns the minute component of the date represented by this instance.
	 * @returns The minute component, expressed as a value between 0 and 59.
	 */
	getMinutes(): number {
		return this._date.getUTCMinutes();
	}

	/**
	 * Returns an integer, between 1 and 12, representing the month.
	 */
	getMonth(): number {
		return this._date.getUTCMonth() + 1;
	}

	/**
	 * Returns the seconds component of the date represented by this instance.
	 * @returns The seconds component, expressed as a value between 0 and 59.
	 */
	getSeconds(): number {
		return this._date.getUTCSeconds();
	}

	/**
	 * Returns the number of ticks that represent the date and time of this instance.
	 * @returns The number of ticks that represent the date and time of this instance.
	 */
	getTicks(): number {
		return this._date.getTime();
	}

	/**
	 * Returns the wall clock as a Date whose LOCAL fields carry the components — the shape the
	 * Intl formatters (which read the default time zone) need. During the browser zone's DST
	 * spring-forward gap the returned Date's fields can shift; only display is affected, the
	 * stored value stays exact.
	 * @returns A Date with the wall-clock components in its local fields.
	 */
	getUtcDate(): Date {
		const d = this._date;
		return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
			d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
	}

	/**
	 * Returns the year component of the date represented by this instance.
	 * @returns The year component.
	 */
	getYear(): number {
		return this._date.getUTCFullYear();
	}

	/**
	 * Determines whether the specified object refers to the same date as the current instance.
	 * @param other The object to compare to this instance.
	 * @returns true if the specified object refers to the same date; otherwise, false.
	 */
	isSameDate(other: DateTime | null | undefined): boolean {
		if (other == null) {
			return false;
		}
		return this.getYear() === other.getYear() && this.getMonth() === other.getMonth() && this.getDay() === other.getDay();
	}

	/**
	 * Returns the start of the day for the current DateTime instance.
	 * @returns A new DateTime representing the start of the day.
	 */
	startOfDay(): DateTime {
		return DateTime.fromComponents(this.getYear(), this.getMonth(), this.getDay());
	}

	/**
	 * Converts the value of the current DateTime object to its equivalent DateOnly representation.
	 * @returns The DateOnly value.
	 */
	toDateOnly(): DateOnly {
		//default fromDate reads UTC fields — where this instance keeps its wall clock
		return DateOnly.fromDate(this._date);
	}

	/**
	 * Converts the value of the current DateTime object to its equivalent DateOnly string representation.
	 * @returns A string that contains the DateOnly representation of the current DateTime object.
	 */
	toDateOnlyString(): string {
		return this._date.toISOString().slice(0, 10);
	}

	/**
	 * Returns the JSON representation of the current DateTime object.
	 * @returns A string that contains the JSON representation of the current DateTime object.
	 */
	toJSON(): string {
		return this._date.toISOString();
	}

	/**
	 * Converts the value of the current DateTime object to its equivalent string representation.
	 * @returns A string that contains the string representation of the current DateTime object.
	 */
	toString(): string {
		return this._date.toISOString();
	}

	/**
	 * Converts the wall-clock value of this instance (treated as UTC) to the equivalent wall-clock
	 * value in the specified IANA time zone. No time zone is stored on the returned object.
	 * @param timeZone An IANA time zone name, e.g. any value from Intl.supportedValuesOf("timeZone").
	 * @returns A new DateTime whose components are this instant as observed in the given time zone.
	 * @throws RangeError if timeZone is not a valid IANA time zone name.
	 */
	toTimeZone(timeZone: string): DateTime {
		const p = partsInTimeZone(this.getTicks(), timeZone);
		return DateTime.fromComponents(p.year, p.month, p.day, p.hour, p.minute, p.second, this.getMilliseconds());
	}

	/**
	 * The inverse of {@link toTimeZone}: interprets the wall-clock value of this instance as a
	 * local time in the specified IANA time zone and returns the equivalent UTC wall-clock value.
	 * During a DST backward transition (an ambiguous local time) the earlier instant wins; a
	 * skipped local time resolves to the instant the zone's offset change lands it on.
	 * @param timeZone An IANA time zone name, e.g. any value from Intl.supportedValuesOf("timeZone").
	 * @returns A new DateTime whose components are the UTC observation of this local time.
	 * @throws RangeError if timeZone is not a valid IANA time zone name.
	 */
	fromTimeZone(timeZone: string): DateTime {
		// Zone offset at a given instant, derived from what a wall clock in the zone shows then.
		const offsetAt = (instant: number): number => {
			const p = partsInTimeZone(instant, timeZone);
			return createFromNumbers(p.year, p.month, p.day, p.hour, p.minute, p.second).getTime()
				- Math.floor(instant / 1000) * 1000;
		};

		// Guess the instant assuming zero offset, then correct with the offset observed at the
		// guess; one more pass settles guesses that landed across a DST transition.
		const wall = this.getTicks();
		const instant = wall - offsetAt(wall - offsetAt(wall));
		return new DateTime(new Date(instant));
	}

	/**
	 * Converts the value of the current DateTime object to its equivalent TimeOnly representation.
	 * @returns A TimeOnly object that represents the time of the day.
	 */
	toTimeOnly(): TimeOnly {
		return TimeOnly.fromComponents(this.getHours(), this.getMinutes(), this.getSeconds(), this.getMilliseconds());
	}

	/**
	 * Converts the value of the current DateTime object to its equivalent TimeOnly string representation.
	 * @returns A string that contains the TimeOnly representation of the current DateTime object.
	 */
	toTimeOnlyString(): string {
		return this._date.toISOString().slice(11, 19);
	}

	/**
	 * Creates a new DateTime object from the specified year, month, day, hours, minutes, seconds, and milliseconds.
	 * @param year The year (1 through 9999).
	 * @param month The month (1 through 12).
	 * @param day The day (1 through the number of days in month).
	 * @param hours The hours (0 through 23).
	 * @param minutes The minutes (0 through 59).
	 * @param seconds The seconds (0 through 59).
	 * @param milliseconds The milliseconds (0 through 999).
	 * @returns A new DateTime object.
	 */
	static fromComponents(year: number, month: number, day: number,
	                      hours: number = 0, minutes: number = 0,
	                      seconds: number = 0, milliseconds: number = 0): DateTime {
		return new DateTime(createFromNumbers(year, month, day, hours, minutes, seconds, milliseconds));
	}

	/**
	 * Creates a new DateTime object from a JavaScript Date object (an instant): the wall clock
	 * is the instant's UTC observation.
	 * @param date The JavaScript Date object.
	 * @returns A new DateTime object.
	 */
	static fromDate(date: Date): DateTime {
		return new DateTime(date);
	}

	/**
	 * Creates a new DateTime object from a DateOnly and TimeOnly object.
	 * @param date The DateOnly object.
	 * @param time The TimeOnly object.
	 * @returns A new DateTime object.
	 */
	static fromDateAndTime(date: DateOnly, time: TimeOnly): DateTime {
		return new DateTime(createFromNumbers(date.getYear(), date.getMonth(), date.getDay(),
			time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds()));
	}

	/**
	 * Creates a new DateTime object from a DateOnly object.
	 * @param date The DateOnly object.
	 * @returns A new DateTime object.
	 */
	static fromDateOnly(date: DateOnly): DateTime {
		return new DateTime(createFromNumbers(date.getYear(), date.getMonth(), date.getDay()));
	}

	/**
	 * Creates a new DateTime object from an ISO 8601 string.
	 * @param isoString The ISO 8601 string.
	 * @returns A new DateTime object.
	 */
	static fromIsoString(isoString: string): DateTime {
		return new DateTime(new Date(isoString));
	}

	/**
	 * Creates a new DateTime object from the specified number of ticks. Exact inverse of
	 * {@link DateTime.getTicks} (the old implementation was off by the local UTC offset).
	 * @param ticks The number of ticks.
	 * @returns A new DateTime object.
	 */
	static fromTicks(ticks: number): DateTime {
		return new DateTime(new Date(ticks));
	}

	/**
	 * Creates a new DateTime object from the specified timestamp.
	 * @param ticks The timestamp.
	 * @returns A new DateTime object.
	 */
	static fromTimestamp(ticks: number): DateTime {
		return new DateTime(new Date(ticks));
	}

	/**
	 * Gets a DateTime object that is set to the current date and time on this computer, expressed as the local time.
	 * @returns A DateTime object whose value is the current local date and time.
	 */
	static localNow(): DateTime {
		//fromUtc re-materializes the local wall clock into UTC fields — the storage layout
		return new DateTime(fromUtc(new Date()));
	}

	/**
	 * Represents the largest possible value of DateTime. This field is read-only.
	 * @returns The largest possible value of DateTime.
	 */
	static maxValue(): DateTime {
		//9999-12-31, mirroring .NET DateTime.MaxValue's date
		return this._maxValue ??= DateTime.fromComponents(9999, 12, 31, 23, 59, 59, 999);
	}

	/**
	 * Represents the smallest possible value of DateTime. This field is read-only.
	 * @returns The smallest possible value of DateTime.
	 */
	static minValue(): DateTime {
		return this._minValue ??= DateTime.fromTimestamp(0);
	}

	/**
	 * Gets a DateTime object that is set to the current date and time on this computer, expressed as the Coordinated Universal Time (UTC).
	 * @returns A DateTime object whose value is the current UTC date and time.
	 */
	static utcNow(): DateTime {
		return new DateTime(new Date());
	}
}
